import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, X, RotateCw, Sparkles, Loader2, FileDown, Presentation, RefreshCw, MapPin, Lock } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import FloatingShapes from "@/components/FloatingShapes";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Quiz() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isPaid = !!user;
  const [params] = useSearchParams();
  const sectionId = params.get("section");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [bundle, setBundle] = useState(0);
  const [maxBundles, setMaxBundles] = useState(1);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [revealed, setRevealed] = useState(false); // flip / show answer
  const [picked, setPicked] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [locked, setLocked] = useState(false);

  const resetRun = () => { setIdx(0); setScore(0); setDone(false); setRevealed(false); setPicked(null); setLocked(false); };

  // Poll a background-generating quiz until it's ready (or failed / times out).
  const pollQuiz = useCallback(async (quizId) => {
    const started = Date.now();
    while (Date.now() - started < 150000) {
      await new Promise((res) => setTimeout(res, 2000));
      const r = await api.get(`/quizzes/${quizId}`);
      if (r.data.status === "ready" || r.data.status === "failed") return r.data;
    }
    throw new Error("The quiz is taking longer than usual — please try again.");
  }, []);

  const fetchQuiz = useCallback(async (b, { silent } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (sectionId) qs.set("section_id", sectionId);
      qs.set("bundle", String(b));
      let data = (await api.post(`/documents/${id}/quiz?${qs.toString()}`)).data;
      // Generation runs in the background — poll until the questions are ready.
      if (data.status === "generating") data = await pollQuiz(data.quiz_id);
      if (data.status === "failed") throw new Error(data.error || "Quiz generation failed");
      setQuiz(data);
      setBundle(b);
      if (data.max_bundles) setMaxBundles(data.max_bundles);
      resetRun();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Couldn't make the quiz";
      if (e?.response?.status === 402) {
        if (b === 0) setPaywall(detail);
        else toast.error(detail);
        api.post("/events/paywall", { feature: b === 0 ? "quiz" : "quiz_refresh", kind: "view", doc_id: id }).catch(()=>{});
      } else {
        toast.error(detail);
        if (b === 0) nav(`/deck/${id}`);
      }
    } finally { setLoading(false); setRefreshing(false); }
  }, [id, sectionId, nav, pollQuiz]);

  useEffect(() => { fetchQuiz(0); }, [fetchQuiz]);

  const downloadExport = async (fmt) => {
    const qs = new URLSearchParams({ fmt, bundle: String(bundle) });
    if (sectionId) qs.set("section_id", sectionId);
    const t = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      await downloadFile(`/documents/${id}/quiz/export?${qs.toString()}`, `loom-quiz.${fmt === "pptx" ? "pptx" : "pdf"}`);
      toast.success(`Downloaded ${fmt.toUpperCase()}`, { id: t });
    } catch (e) {
      toast.error(e?.message || "Export failed", { id: t });
    }
  };

  const moreSetsAvailable = maxBundles > 1 && bundle + 1 < maxBundles;
  const newBundle = () => fetchQuiz(bundle + 1, { silent: true });

  if (loading) {
    return (
      <div className="relative min-h-screen grain" data-testid="quiz-loading">
        <FloatingShapes/><Nav/>
        <main className="relative z-10 max-w-2xl mx-auto px-6 pt-40 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto"/>
          <h1 className="font-display text-4xl mt-6">Mixing questions for you</h1>
          <p className="font-hand text-stone-500 text-2xl mt-2">flashcards · MCQs · recall prompts</p>
        </main>
      </div>
    );
  }
  if (paywall) {
    return (
      <div className="relative min-h-screen grain" data-testid="quiz-paywall">
        <FloatingShapes/><Nav/>
        <main className="relative z-10 max-w-xl mx-auto px-6 pt-40 text-center">
          <div className="sticky-card p-12" style={{ background: "linear-gradient(180deg,#FEF08A55 0%,#fff 60%)" }}>
            <Sparkles className="w-12 h-12 text-amber-500 mx-auto"/>
            <h1 className="font-display text-4xl mt-6">Quizzes are a Pro feature</h1>
            <p className="text-stone-700 mt-4" data-testid="quiz-paywall-message">{paywall}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={()=>{ api.post("/events/paywall", { feature: "quiz", kind: "upgrade_click", doc_id: id }).catch(()=>{}); nav("/pricing"); }} className="btn-primary inline-flex items-center gap-2" data-testid="quiz-paywall-upgrade-button">
                <Sparkles className="w-4 h-4"/> See plans
              </button>
              <Link to={`/deck/${id}`} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-paywall-back-button">
                <ArrowLeft className="w-4 h-4"/> Back to deck
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }
  if (!quiz || !quiz.items?.length) return null;

  const total = quiz.items.length;
  const item = quiz.items[idx];
  const totalQuestions = quiz.total_questions || total;
  const lockedCount = quiz.locked_questions || 0;

  const next = () => {
    setRevealed(false); setPicked(null);
    if (idx + 1 >= total) {
      if (lockedCount > 0) {
        setLocked(true);
        api.post("/events/paywall", { feature: "quiz_locked", kind: "view", doc_id: id }).catch(()=>{});
      } else {
        setDone(true);
      }
    } else setIdx(idx + 1);
  };

  const submitMCQ = (i) => {
    if (revealed) return;
    setPicked(i); setRevealed(true);
    if (i === item.correct_index) setScore((s)=>s+1);
  };

  const markRecall = (ok) => {
    if (ok) setScore((s)=>s+1);
    // briefly show the result then advance
    setTimeout(() => next(), 350);
  };

  const flipCard = () => {
    if (!revealed) {
      setRevealed(true);
    } else {
      // second click = "I got it"
      setScore((s)=>s+1);
      next();
    }
  };

  const restart = resetRun;

  return (
    <div className="relative min-h-screen grain" data-testid="quiz-page">
      <FloatingShapes/><Nav/>
      <main className="relative z-10 max-w-2xl mx-auto px-6 pt-28 pb-24">
        <div className="flex items-center justify-between">
          <Link to={`/deck/${id}`} className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1" data-testid="back-to-deck">
            <ArrowLeft className="w-4 h-4"/> Back to deck
          </Link>
          <div className="text-sm text-stone-500" data-testid="quiz-progress">
            {bundle > 0 && <span className="tag-pill mr-2" data-testid="quiz-bundle-indicator">set #{bundle+1}</span>}
            {locked ? "Locked" : done ? "Done" : `${idx+1} / ${totalQuestions}`} · <span className="text-indigo-600">{score} ✓</span>
          </div>
        </div>

        {locked ? (
          <motion.div initial={{opacity:0, y:30}} animate={{opacity:1, y:0}}
                      className="sticky-card p-12 mt-10 text-center" data-testid="quiz-locked-wall"
                      style={{ background: "linear-gradient(180deg,#FEF08A55 0%,#fff 60%)" }}>
            <Lock className="w-12 h-12 text-amber-500 mx-auto"/>
            <h1 className="font-display text-4xl mt-6" data-testid="quiz-locked-title">
              {lockedCount} more questions are locked
            </h1>
            <p className="text-stone-700 mt-3 max-w-md mx-auto" data-testid="quiz-locked-message">
              You've previewed {total} of {totalQuestions} questions. Upgrade to Pro or Lifetime to unlock
              the full {totalQuestions}-question quiz — plus fresh question sets, PDF/PPT exports and more.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={()=>{ api.post("/events/paywall", { feature: "quiz_locked", kind: "upgrade_click", doc_id: id }).catch(()=>{}); nav("/pricing"); }} className="btn-primary inline-flex items-center gap-2" data-testid="quiz-locked-upgrade-button">
                <Sparkles className="w-4 h-4"/> Unlock all {totalQuestions} questions
              </button>
              <button onClick={restart} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-locked-retry-button"><RotateCw className="w-4 h-4"/> Retry preview</button>
              <button onClick={()=>nav(`/deck/${id}`)} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-locked-back-button"><ArrowLeft className="w-4 h-4"/> Back to deck</button>
            </div>
          </motion.div>
        ) : !done ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{opacity:0, y:30, rotate: -1.5}} animate={{opacity:1, y:0, rotate: 0}}
              exit={{opacity:0, y:-30, rotate: 1.5}} transition={{duration:0.5}}
              className="mt-10"
            >
              <div className="flex items-center gap-3">
                <span className="tag-pill" data-testid="quiz-item-type">
                  {item.type === "flashcard" && "Flashcard"}
                  {item.type === "mcq"       && "Multiple choice"}
                  {item.type === "recall"    && "Recall prompt"}
                </span>
                {item.type === "flashcard" && <span className="font-hand text-stone-500 text-lg">click the card to flip</span>}
              </div>

              {item.type === "flashcard" && (
                <button
                  onClick={flipCard}
                  className="mt-6 perspective w-full"
                  data-testid="flashcard-flip-container"
                  aria-label="Flip flashcard"
                >
                  <div className={`relative w-full h-[340px] preserve-3d transition-transform duration-700 ${revealed ? "rotate-y-180" : ""}`}>
                    <div className="absolute inset-0 backface-hidden sticky-card flex flex-col items-center justify-center p-10 text-center" data-testid="flashcard-front">
                      <span className="tag-pill mb-5">question</span>
                      <p className="font-display text-2xl sm:text-3xl leading-tight">{item.question}</p>
                      <span className="font-hand text-stone-500 text-lg mt-6">tap to reveal</span>
                    </div>
                    <div className="absolute inset-0 backface-hidden rotate-y-180 sticky-card flex flex-col items-center justify-center p-10 text-center"
                         style={{ background: "linear-gradient(180deg, #DDD6FE55 0%, #fff 70%)" }} data-testid="flashcard-back">
                      <span className="tag-pill mb-5">answer</span>
                      <p className="text-stone-800 text-lg leading-relaxed">{item.answer}</p>
                      <span className="font-hand text-stone-500 text-lg mt-6">tap again if you got it</span>
                    </div>
                  </div>
                </button>
              )}

              {item.type === "mcq" && (
                <div className="mt-6 sticky-card p-8" data-testid="mcq-card">
                  <p className="font-display text-2xl leading-snug">{item.question}</p>
                  <div className="mt-5 space-y-3">
                    {(item.options||[]).map((opt, i) => {
                      const isCorrect = revealed && i === item.correct_index;
                      const isWrongPick = revealed && i === picked && i !== item.correct_index;
                      return (
                        <button
                          key={i}
                          onClick={()=>submitMCQ(i)}
                          disabled={revealed}
                          data-testid={`quiz-mcq-option-${i}`}
                          className={`w-full text-left px-5 py-4 rounded-2xl border transition-all
                            ${isCorrect ? "bg-emerald-50 border-emerald-300" :
                             isWrongPick ? "bg-rose-50 border-rose-300" :
                             "bg-white border-stone-200 hover:border-indigo-300 hover:-translate-y-0.5"}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-hand text-xl text-stone-400">{String.fromCharCode(65+i)}</span>
                            <span className="text-stone-800">{opt}</span>
                            {isCorrect && <Check className="w-4 h-4 text-emerald-600 ml-auto"/>}
                            {isWrongPick && <X className="w-4 h-4 text-rose-600 ml-auto"/>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {revealed && item.answer && (
                    <div className="mt-5 text-sm text-stone-600 font-hand text-lg">
                      tip: <span className="marker">{item.answer}</span>
                    </div>
                  )}
                </div>
              )}

              {item.type === "recall" && (
                <div className="mt-6 sticky-card p-8" data-testid="recall-card">
                  <p className="font-display text-2xl leading-snug">{item.question}</p>
                  <p className="font-hand text-stone-500 text-lg mt-1">say it aloud, write it down, then peek</p>
                  {!revealed ? (
                    <button onClick={()=>setRevealed(true)} className="btn-ghost mt-6" data-testid="recall-reveal-button">Show model answer</button>
                  ) : (
                    <>
                      <div className="mt-5 p-5 rounded-2xl bg-indigo-50/70 border border-indigo-100 text-stone-800 leading-relaxed">{item.answer}</div>
                      <div className="mt-5 flex gap-3">
                        <button onClick={()=>markRecall(false)} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="recall-fail-button"><X className="w-4 h-4"/> Missed it</button>
                        <button onClick={()=>markRecall(true)} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="recall-got-button"><Check className="w-4 h-4"/> Got it</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* answer reference — shown outside the card */}
              {(item.section || item.page_range) && (
                <div className="mt-4 flex items-center gap-2 text-sm text-stone-500" data-testid="quiz-item-reference">
                  <MapPin className="w-4 h-4 text-indigo-400"/>
                  <span>
                    {item.section && item.section !== "—" && <span>From <b className="text-stone-700">{item.section}</b></span>}
                    {item.page_range && <span>{item.section && item.section !== "—" ? " · " : ""}p. {item.page_range}</span>}
                  </span>
                </div>
              )}

              {/* next button — for MCQ only (recall auto-advances after grading) */}
              {item.type === "mcq" && revealed && (
                <div className="mt-6 flex justify-end">
                  <button onClick={next} className="btn-primary inline-flex items-center gap-2" data-testid="quiz-next-button">
                    Next →
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        ) : (
          <motion.div initial={{opacity:0, y:30}} animate={{opacity:1, y:0}}
                      className="sticky-card p-12 mt-10 text-center" data-testid="quiz-results">
            <div className="text-6xl">{score === total ? "🌟" : score >= total*0.7 ? "🎉" : "📚"}</div>
            <h1 className="font-display text-5xl mt-6">{score} / {total}</h1>
            <p className="font-hand text-stone-500 text-2xl mt-2">
              {score === total ? "perfect run — go teach someone" :
               score >= total*0.7 ? "strong recall — one more pass and it's yours" :
               "the details want another walk through the rooms"}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={restart} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-retry-button"><RotateCw className="w-4 h-4"/> Retry</button>
              {moreSetsAvailable && (
                <button onClick={newBundle} disabled={refreshing} className="btn-primary inline-flex items-center gap-2" data-testid="quiz-new-set-button">
                  {refreshing ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
                  Fresh questions ({bundle + 1}/{maxBundles})
                </button>
              )}
              {isPaid && (
                <>
                  <button onClick={()=>downloadExport("pdf")} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-export-pdf-button"><FileDown className="w-4 h-4"/> Export PDF</button>
                  <button onClick={()=>downloadExport("pptx")} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-export-ppt-button"><Presentation className="w-4 h-4"/> Export PPT</button>
                  <Link to={`/dashboard`} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-back-dashboard-button">My decks</Link>
                </>
              )}
              <button onClick={()=>nav(`/deck/${id}`)} className="btn-primary inline-flex items-center gap-2" data-testid="quiz-back-deck-button">Walk the deck again →</button>
              {!isPaid && (
                <button onClick={()=>nav("/pricing")} className="btn-ghost inline-flex items-center gap-2" data-testid="quiz-upgrade-button">
                  <Sparkles className="w-4 h-4"/> Unlock exports & more
                </button>
              )}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
